import type { JSX } from 'react';
import {
  availableResponseModelsSchema,
  REASONING_EFFORTS,
  RESPONSE_MODELS,
  DEFAULT_RESPONSE_OUTPUT_TOKENS,
  reasoningEffortSchema,
  responseModelEntry,
  responseModelIdSchema,
  responseProcessingModeSchema,
  type BotScope,
  type ResponseModelSettings,
} from '@cryptology-assist/domain';
import { botPath } from './api';
import { ErrorNotice, Field } from './components';
import { useRemote } from './hooks';
import type { Copy } from './copy';

export function ModelSettings({
  scope,
  value,
  onChange,
  copy,
}: {
  scope: BotScope;
  value: ResponseModelSettings;
  onChange: (value: ResponseModelSettings) => void;
  copy: Copy;
}): JSX.Element {
  const discovery = useRemote(
    botPath(scope, 'configuration/models'),
    availableResponseModelsSchema
  );
  // A setting the selected model does not offer stays visible, so the
  // operator changes it; publication rejects it.
  const offered = responseModelEntry(value.model);
  return (
    <section className="form-section">
      <h2>{copy.modelSettings}</h2>
      <p>{copy.modelSettingsHelp}</p>
      <div className="form-grid">
        <div>
          <Field label={copy.responseModel}>
            <select
              value={value.model}
              onChange={(event) =>
                onChange({
                  ...value,
                  model: responseModelIdSchema.parse(event.target.value),
                })
              }
            >
              {RESPONSE_MODELS.map((model) => {
                const available = discovery.data?.models.find(
                  (entry) => entry.model === model.model
                )?.available;
                return (
                  <option
                    key={model.model}
                    value={model.model}
                    disabled={!available && model.model !== value.model}
                  >
                    {model.displayName}
                    {available === false ? ` · ${copy.modelUnavailable}` : ''}
                  </option>
                );
              })}
            </select>
          </Field>
          <p className="small muted field-hint">{copy.modelDiscoveryHelp}</p>
        </div>
        <div>
          <Field label={copy.reasoningEffort}>
            <select
              value={value.reasoningEffort}
              onChange={(event) =>
                onChange({
                  ...value,
                  reasoningEffort: reasoningEffortSchema.parse(
                    event.target.value
                  ),
                })
              }
            >
              {REASONING_EFFORTS.map((effort) => {
                const supported = offered.reasoningEfforts.includes(effort);
                return (
                  <option
                    key={effort}
                    value={effort}
                    disabled={!supported && effort !== value.reasoningEffort}
                  >
                    {copy[`reasoning_${effort}`]}
                    {supported ? '' : ` · ${copy.modelSettingNotOffered}`}
                  </option>
                );
              })}
            </select>
          </Field>
          <p className="small muted field-hint">{copy.reasoningHelp}</p>
        </div>
        <div>
          <Field label={copy.responseProcessingMode}>
            <select
              value={value.processingMode ?? 'standard'}
              onChange={(event) =>
                onChange({
                  ...value,
                  processingMode: responseProcessingModeSchema.parse(
                    event.target.value
                  ),
                })
              }
            >
              <option value="standard">{copy.processingStandard}</option>
              <option
                value="fast"
                disabled={
                  !offered.fastProcessing && value.processingMode !== 'fast'
                }
              >
                {copy.processingFast}
                {offered.fastProcessing
                  ? ''
                  : ` · ${copy.modelSettingNotOffered}`}
              </option>
            </select>
          </Field>
          <p className="small muted field-hint">
            {copy.responseProcessingModeHelp}
          </p>
        </div>
        <div>
          <Field label={copy.responseOutputTokens}>
            <input
              type="number"
              min={1024}
              max={32000}
              step={1}
              value={
                Number.isNaN(value.maxOutputTokens)
                  ? ''
                  : (value.maxOutputTokens ?? DEFAULT_RESPONSE_OUTPUT_TOKENS)
              }
              onChange={(event) =>
                onChange({
                  ...value,
                  maxOutputTokens: event.target.valueAsNumber,
                })
              }
            />
          </Field>
          <p className="small muted field-hint">
            {copy.responseOutputTokensHelp}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={discovery.reload}
        disabled={discovery.loading}
      >
        {discovery.loading ? copy.modelChecking : copy.refreshModels}
      </button>
      <ErrorNotice error={discovery.error} copy={copy} />
    </section>
  );
}
